import { useEffect, useState } from "react";
import { Buildings, ChartBar, Clock } from "@phosphor-icons/react";
import api from "@/lib/api";
import { STATUSES } from "@/lib/constants";

export default function AdminStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats({ total: 0, months: [], categories: [], statuses: {} }));
  }, []);

  if (!stats) {
    return <div className="h-48 animate-pulse rounded-3xl bg-black/5" data-testid="stats-loading" />;
  }

  const maxMonth = Math.max(1, ...stats.months.map((m) => m.count));
  const maxCat = Math.max(1, ...stats.categories.map((c) => c.count));
  const allSubs = stats.sub_buildings || [];
  const subStats = allSubs.filter((b) => b.name !== "Non précisé" || allSubs.length > 1);
  const maxSub = Math.max(1, ...subStats.map((b) => b.count));
  const fmtDelay = (h) =>
    h == null ? null : h < 24 ? `${Math.max(1, Math.round(h))} h` : `${String(Math.round((h / 24) * 10) / 10).replace(".", ",")} j`;

  return (
    <div data-testid="admin-stats-page">
      <h2 className="font-display text-2xl font-extrabold tracking-tight">Statistiques</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {stats.total} signalement(s) au total pour cette copropriété.
      </p>

      <div className="mt-6 rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="stats-months-chart">
        <p className="flex items-center gap-2 text-sm font-bold">
          <ChartBar size={18} weight="duotone" className="text-primary" />
          Signalements par mois (6 derniers mois)
        </p>
        <div className="mt-6 flex h-44 items-end gap-3">
          {stats.months.map((m) => (
            <div key={m.key} className="flex h-full flex-1 flex-col items-center justify-end gap-2" data-testid={`stats-month-${m.key}`}>
              <span className="text-xs font-extrabold text-foreground">{m.count}</span>
              <div
                className="w-full rounded-t-xl bg-primary/85 transition-all duration-500"
                style={{ height: `${Math.max(3, Math.round((m.count / maxMonth) * 120))}px` }}
              />
              <span className="text-center text-[10px] font-semibold leading-tight text-muted-foreground">{m.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="stats-categories">
          <p className="text-sm font-bold">Par catégorie</p>
          {stats.categories.length === 0 && (
            <p className="mt-4 text-xs text-muted-foreground" data-testid="stats-categories-empty">Aucun signalement pour le moment.</p>
          )}
          <div className="mt-4 space-y-3">
            {stats.categories.map((c) => (
              <div key={c.key} data-testid={`stats-category-${c.key}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold">{c.label}</span>
                  <span className="flex items-center gap-2 font-extrabold">
                    {fmtDelay(c.avg_resolution_hours) && (
                      <span
                        data-testid={`stats-category-${c.key}-delay`}
                        className="inline-flex items-center gap-1 rounded-full bg-black/5 px-2 py-0.5 text-[10px] font-bold text-muted-foreground"
                      >
                        <Clock size={10} weight="duotone" />
                        {fmtDelay(c.avg_resolution_hours)}
                      </span>
                    )}
                    {c.count}
                  </span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-black/5">
                  <div
                    className="h-full rounded-full bg-primary/80 transition-all duration-500"
                    style={{ width: `${Math.round((c.count / maxCat) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="stats-statuses">
          <p className="text-sm font-bold">Par statut</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.entries(STATUSES).map(([key, s]) => (
              <span
                key={key}
                data-testid={`stats-status-${key}`}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold"
                style={{ backgroundColor: s.bg, color: s.text }}
              >
                {s.label} · {stats.statuses[key] || 0}
              </span>
            ))}
          </div>
          {fmtDelay(stats.avg_resolution_hours) && (
            <p className="mt-4 flex items-center gap-2 border-t border-black/5 pt-4 text-xs text-muted-foreground" data-testid="stats-avg-resolution">
              <Clock size={14} weight="duotone" className="shrink-0 text-primary" />
              Délai moyen de résolution : <strong className="text-foreground">{fmtDelay(stats.avg_resolution_hours)}</strong>
            </p>
          )}
        </div>
      </div>

      {subStats.length > 0 && (
        <div className="mt-4 rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="stats-buildings">
          <p className="flex items-center gap-2 text-sm font-bold">
            <Buildings size={18} weight="duotone" className="text-primary" />
            Par immeuble
          </p>
          <div className="mt-4 space-y-4">
            {subStats.map((b) => (
              <div key={b.name} data-testid={`stats-building-${b.name}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold">{b.name}</span>
                  <span className="font-extrabold">{b.count}</span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-black/5">
                  <div
                    className="h-full rounded-full bg-primary/80 transition-all duration-500"
                    style={{ width: `${Math.round((b.count / maxSub) * 100)}%` }}
                  />
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {fmtDelay(b.avg_resolution_hours) && (
                    <span
                      data-testid={`stats-building-${b.name}-delay`}
                      className="inline-flex items-center gap-1 rounded-full bg-black/5 px-2 py-0.5 text-[10px] font-bold text-muted-foreground"
                    >
                      <Clock size={10} weight="duotone" />
                      Délai moyen : {fmtDelay(b.avg_resolution_hours)}
                    </span>
                  )}
                  {Object.entries(STATUSES).map(([key, s]) =>
                    b.statuses[key] ? (
                      <span
                        key={key}
                        data-testid={`stats-building-${b.name}-status-${key}`}
                        className="rounded-full px-2 py-0.5 text-[10px] font-bold"
                        style={{ backgroundColor: s.bg, color: s.text }}
                      >
                        {s.label} · {b.statuses[key]}
                      </span>
                    ) : null
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
